import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import {
  cn,
  progressVariants,
  progressIndicatorVariants,
  type ProgressVariants,
  type ProgressIndicatorVariants,
} from '../../../utils';

export interface ProgressProps
  extends ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
    ProgressVariants,
    ProgressIndicatorVariants {}

/**
 * The value handed to the progressbar root, so it exposes aria-valuenow and a
 * determinate state (agion/Agion#3028). Radix refuses a value outside 0..max,
 * so the bar clamps it as the indicator already does; a missing or non-finite
 * value stays indeterminate. The accessible name comes from the caller
 * (aria-label or aria-labelledby): a bar has no visible text of its own.
 */
function rootValue(value: number | null | undefined, max: number | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const top = typeof max === 'number' && max > 0 ? max : 100;
  return Math.min(Math.max(value, 0), top);
}

export const Progress = forwardRef<
  ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ className, value, max, size, variant, ...props }, ref) => (
  <ProgressPrimitive.Root
    ref={ref}
    className={cn(progressVariants({ size }), className)}
    value={rootValue(value, max)}
    max={max}
    {...props}
  >
    <ProgressPrimitive.Indicator
      className={cn(progressIndicatorVariants({ variant }))}
      style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
    />
  </ProgressPrimitive.Root>
));
Progress.displayName = ProgressPrimitive.Root.displayName;
